import { api } from "@/utils/axiosInterceptor";
import type {
  Quotation,
  QuotationListItem,
  SalesOrder,
  SalesOrderListItem,
  DeliveryOrder,
  DeliveryOrderListItem,
  CreateQuotationRequest,
  CreateSalesOrderRequest,
  CreateDeliveryOrderRequest,
  ConvertQuotationToSalesOrderRequest,
  CreateDeliveryOrderFromSalesOrderRequest,
  SalesDocumentApiResponse,
  SalesDocumentListResponse,
} from "@/types/salesDocuments";

const QUOTATIONS_BASE_URL = "/quotations";
const SALES_ORDERS_BASE_URL = "/sales-orders";
const DELIVERY_ORDERS_BASE_URL = "/delivery-orders";

// ==================== QUOTATIONS ====================

export const quotationsApi = {
  // Get all quotations
  getAll: async (params?: {
    search?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.dateFrom) queryParams.append("dateFrom", params.dateFrom);
    if (params?.dateTo) queryParams.append("dateTo", params.dateTo);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());

    const response = await api.get<SalesDocumentListResponse<QuotationListItem>>(
      `${QUOTATIONS_BASE_URL}?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get single quotation by ID
  getById: async (id: string): Promise<Quotation> => {
    const response = await api.get<Quotation>(
      `${QUOTATIONS_BASE_URL}/${id}`
    );
    return response.data;
  },

  // Create new quotation
  create: async (data: CreateQuotationRequest) => {
    const response = await api.post<SalesDocumentApiResponse<Quotation>>(
      QUOTATIONS_BASE_URL,
      data
    );
    return response.data;
  },

  // Update quotation
  update: async (id: string, data: Partial<CreateQuotationRequest>) => {
    const response = await api.put<SalesDocumentApiResponse<Quotation>>(
      `${QUOTATIONS_BASE_URL}/${id}`,
      data
    );
    return response.data;
  },

  // Delete quotation
  delete: async (id: string) => {
    const response = await api.delete<SalesDocumentApiResponse<void>>(
      `${QUOTATIONS_BASE_URL}/${id}`
    );
    return response.data;
  },

  // Convert quotation to sales order
  convertToSalesOrder: async (data: ConvertQuotationToSalesOrderRequest) => {
    const response = await api.post<SalesDocumentApiResponse<SalesOrder>>(
      `${QUOTATIONS_BASE_URL}/convert-to-sales-order`,
      data
    );
    return response.data;
  },

  // Update quotation status
  updateStatus: async (id: string, status: string) => {
    const response = await api.patch<SalesDocumentApiResponse<Quotation>>(
      `${QUOTATIONS_BASE_URL}/${id}/status`,
      { status }
    );
    return response.data;
  },

  // Clone quotation
  clone: async (id: string) => {
    const response = await api.post<SalesDocumentApiResponse<Quotation>>(
      `${QUOTATIONS_BASE_URL}/${id}/clone`
    );
    return response.data;
  },

  // Send quotation emails to buyers
  sendEmail: async (ids: string[]) => {
    const response = await api.post<{ success: boolean; message: string; count: number }>(
      `${QUOTATIONS_BASE_URL}/send-email`,
      { ids }
    );
    return response.data;
  },
};

// ==================== SALES ORDERS ====================

export const salesOrdersApi = {
  // Get all sales orders
  getAll: async (params?: {
    search?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.dateFrom) queryParams.append("dateFrom", params.dateFrom);
    if (params?.dateTo) queryParams.append("dateTo", params.dateTo);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());

    const response = await api.get<SalesDocumentListResponse<SalesOrderListItem>>(
      `${SALES_ORDERS_BASE_URL}?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get single sales order by ID
  getById: async (id: string): Promise<SalesOrder> => {
    const response = await api.get<SalesOrder>(
      `${SALES_ORDERS_BASE_URL}/${id}`
    );
    return response.data;
  },

  // Create new sales order
  create: async (data: CreateSalesOrderRequest) => {
    const response = await api.post<SalesDocumentApiResponse<SalesOrder>>(
      SALES_ORDERS_BASE_URL,
      data
    );
    return response.data;
  },

  // Update sales order
  update: async (id: string, data: Partial<CreateSalesOrderRequest>) => {
    const response = await api.put<SalesDocumentApiResponse<SalesOrder>>(
      `${SALES_ORDERS_BASE_URL}/${id}`,
      data
    );
    return response.data;
  },

  // Delete sales order
  delete: async (id: string) => {
    const response = await api.delete<SalesDocumentApiResponse<void>>(
      `${SALES_ORDERS_BASE_URL}/${id}`
    );
    return response.data;
  },

  // Update sales order status
  updateStatus: async (id: string, status: string) => {
    const response = await api.patch<SalesDocumentApiResponse<SalesOrder>>(
      `${SALES_ORDERS_BASE_URL}/${id}/status`,
      { status }
    );
    return response.data;
  },

  // Clone sales order
  clone: async (id: string) => {
    const response = await api.post<SalesDocumentApiResponse<SalesOrder>>(
      `${SALES_ORDERS_BASE_URL}/${id}/clone`
    );
    return response.data;
  },

  // Send sales order emails to buyers
  sendEmail: async (ids: string[]) => {
    const response = await api.post<{ success: boolean; message: string; count: number }>(
      `${SALES_ORDERS_BASE_URL}/send-email`,
      { ids }
    );
    return response.data;
  },

  // Get delivery orders for a sales order
  getDeliveryOrders: async (id: string) => {
    const response = await api.get<SalesDocumentListResponse<DeliveryOrderListItem>>(
      `${SALES_ORDERS_BASE_URL}/${id}/delivery-orders`
    );
    return response.data;
  },
};

// ==================== DELIVERY ORDERS ====================

export const deliveryOrdersApi = {
  // Get all delivery orders
  getAll: async (params?: {
    search?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    salesOrderId?: string;
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.dateFrom) queryParams.append("dateFrom", params.dateFrom);
    if (params?.dateTo) queryParams.append("dateTo", params.dateTo);
    if (params?.salesOrderId) queryParams.append("salesOrderId", params.salesOrderId);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());

    const response = await api.get<SalesDocumentListResponse<DeliveryOrderListItem>>(
      `${DELIVERY_ORDERS_BASE_URL}?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get single delivery order by ID
  getById: async (id: string): Promise<DeliveryOrder> => {
    const response = await api.get<DeliveryOrder>(
      `${DELIVERY_ORDERS_BASE_URL}/${id}`
    );
    return response.data;
  },

  // Create new delivery order
  create: async (data: CreateDeliveryOrderRequest) => {
    const response = await api.post<SalesDocumentApiResponse<DeliveryOrder>>(
      DELIVERY_ORDERS_BASE_URL,
      data
    );
    return response.data;
  },

  // Update delivery order
  update: async (id: string, data: Partial<CreateDeliveryOrderRequest>) => {
    const response = await api.put<SalesDocumentApiResponse<DeliveryOrder>>(
      `${DELIVERY_ORDERS_BASE_URL}/${id}`,
      data
    );
    return response.data;
  },

  // Delete delivery order
  delete: async (id: string) => {
    const response = await api.delete<SalesDocumentApiResponse<void>>(
      `${DELIVERY_ORDERS_BASE_URL}/${id}`
    );
    return response.data;
  },

  // Update delivery order status
  updateStatus: async (id: string, status: string) => {
    const response = await api.patch<SalesDocumentApiResponse<DeliveryOrder>>(
      `${DELIVERY_ORDERS_BASE_URL}/${id}/status`,
      { status }
    );
    return response.data;
  },

  // Clone delivery order
  clone: async (id: string) => {
    const response = await api.post<SalesDocumentApiResponse<DeliveryOrder>>(
      `${DELIVERY_ORDERS_BASE_URL}/${id}/clone`
    );
    return response.data;
  },

  // Send delivery order emails to buyers
  sendEmail: async (ids: string[]) => {
    const response = await api.post<{ success: boolean; message: string; count: number }>(
      `${DELIVERY_ORDERS_BASE_URL}/send-email`,
      { ids }
    );
    return response.data;
  },

  // Create delivery order from sales order
  createFromSalesOrder: async (data: CreateDeliveryOrderFromSalesOrderRequest) => {
    const response = await api.post<SalesDocumentApiResponse<DeliveryOrder>>(
      "/delivery-order/create-from-sales-order",
      data
    );
    return response.data;
  },
};

export default {
  quotations: quotationsApi,
  salesOrders: salesOrdersApi,
  deliveryOrders: deliveryOrdersApi,
};
